import {StateMachine} from 'javascript-state-machine';

import {AnalyticsStateMachine} from '../../analyticsStateMachines/AnalyticsStateMachine';
import {
  customStateMachineErrorCallback,
  logMissingCallbackWarning,
  logStateMachineTransition,
  on,
} from '../../analyticsStateMachines/stateMachineUtils';
import type {HeartbeatService} from '../../core/HeartbeatService';
import {Event, JSM_INITIAL_EVENT} from '../../enums/Event';
import type {AnalyticsConfig} from '../../types/AnalyticsConfig';
import type {AnalyticsStateMachineOptions} from '../../types/AnalyticsStateMachineOptions';
import type {AnalyticsEventBase} from '../../types/EventData';
import type {StateMachineCallbacks} from '../../types/StateMachineCallbacks';
import {EventDebugging} from '../../utils/EventDebugging';
import {logger} from '../../utils/Logger';
import {isNumber} from '../../utils/Utils';

export enum State {
  END = 'END',
  ERROR = 'ERROR',
  PAUSE = 'PAUSE',
  PLAYING = 'PLAYING',
  READY = 'READY',
  SETUP = 'SETUP',
  SOURCE_LOADED = 'SOURCE_LOADED',
  STARTUP = 'STARTUP',
  STARTUP_FAILURE = 'STARTUP_FAILURE',
}

export class BitmovinPwxAnalyticsStateMachine extends AnalyticsStateMachine {
  private enabledDebugging = false;
  private debuggingStates: EventDebugging[] = [];

  constructor(
    stateMachineCallbacks: StateMachineCallbacks,
    playingHeartbeatService: HeartbeatService,
    opts: AnalyticsStateMachineOptions,
  ) {
    super(stateMachineCallbacks, playingHeartbeatService, opts);
  }

  getAllStatesBut(states: string[]) {
    return this.getAllStates().filter((i) => states.indexOf(i) < 0);
  }

  getAllStates() {
    return Object.keys(State).map((key) => State[key]);
  }

  override onSsaiPlaybackInteraction(_timestamp: number, _eventObject: AnalyticsEventBase): void {
    // TODO: [AN-4289] Implement SSAI ad handling for PWX
  }

  override createStateMachine(opts: AnalyticsStateMachineOptions): StateMachine {
    return StateMachine.create({
      // custom initial state and custom JSM initial event name
      // initial event is used by JSM for the initial state transition 'none' -> 'SETUP'
      initial: {state: State.SETUP, event: JSM_INITIAL_EVENT},
      error: customStateMachineErrorCallback,
      events: [
        {
          name: Event.SOURCE_LOADED,
          from: [State.SETUP, State.ERROR],
          to: State.READY,
        },
        on(Event.READY).stayIn(State.READY),
        on(Event.PAUSE).stayIn(State.READY),
        on(Event.TIMECHANGED).stayIn(State.READY),
        {name: Event.PLAY, from: State.READY, to: State.STARTUP},
        {name: Event.PLAYING, from: State.READY, to: State.PLAYING},

        on(Event.PLAY).stayIn(State.STARTUP),
        on(Event.PLAYING).stayIn(State.STARTUP),
        {name: Event.TIMECHANGED, from: State.STARTUP, to: State.PLAYING},
        on(Event.READY).stayIn(State.STARTUP),
        {name: Event.PAUSE, from: State.STARTUP, to: State.READY},

        on(Event.PLAYING).stayIn(State.PLAYING),
        on(Event.TIMECHANGED).stayIn(State.PLAYING),

        {name: Event.PAUSE, from: State.PLAYING, to: State.PAUSE},
        on(Event.TIMECHANGED).stayIn(State.PAUSE),
        on(Event.PAUSE).stayIn(State.PAUSE),
        {name: Event.PLAY, from: State.PAUSE, to: State.PLAYING},
        {name: Event.PLAYING, from: State.PAUSE, to: State.PLAYING},

        {name: Event.END, from: State.PLAYING, to: State.END},
        {name: Event.END, from: State.PAUSE, to: State.END},
        // when replay is triggered (play button is clicked once video finished),
        // we should start a new session, so we should go to STARTUP state
        {name: Event.PLAYING, from: State.END, to: State.STARTUP},

        on(Event.TIMECHANGED).stayIn(State.END),
        on(Event.END).stayIn(State.END),

        {name: Event.PLAY, from: State.END, to: State.STARTUP},
        {name: Event.ERROR, from: State.STARTUP, to: State.STARTUP_FAILURE},
        {name: Event.ERROR, from: this.getAllStatesBut([State.STARTUP]), to: State.ERROR},

        {name: Event.UNLOAD, from: this.getAllStatesBut([State.STARTUP]), to: State.END},
        {name: Event.UNLOAD, from: [State.STARTUP], to: State.STARTUP_FAILURE},
      ],

      // Keep callbacks sorted by order in which they are called by JSM library (and after order, sorted by name)
      //
      // Do not use convenience, shortened callbacks
      //  - instead of `onEVENT` use `onafterEVENT`
      //  - instead of `onSTATE` use `onenterSTATE`
      //
      // see: https://github.com/jakesgordon/javascript-state-machine/blob/2.4.0/README.md#callbacks
      callbacks: {
        [`onleave${State.PLAYING}`]: () => {
          this.playingHeartbeatService.stopHeartbeat();
        },

        onleavestate: (event, from, to, eventTimestamp, eventData) => {
          logStateMachineTransition('LEAVE', event, from, to);

          // Skip this JSM callback for JSM initial event
          // This event is fired when the state machine is constructed,
          // to transition us from 'none' to custom initial 'SETUP' state
          if (event === JSM_INITIAL_EVENT) {
            return;
          }

          // Skip this JSM callback when an JSM event without a timestamp is called,
          // because it would cause NPE in analytics code (state duration calculation etc.).
          // This probably has happened because the state machine was not used correctly,
          // aka. JSM event was not called via `callEvent` method as it should be!
          if (!eventTimestamp) {
            return;
          }

          this.addStatesToLog(event, from, to, eventTimestamp, eventData);
          const stateDuration = eventTimestamp - this.onEnterStateTimestamp;
          const isStartup = from === State.STARTUP && to === State.PLAYING;
          this.maybeSetOrClearVideoStartTimeouts(from, to);

          if (isStartup) {
            this.stateMachineCallbacks.startup(stateDuration, State.STARTUP.toLowerCase());
          }

          const lowerCaseFrom = String(from).toLowerCase();
          if (event === Event.UNLOAD) {
            this.stateMachineCallbacks.unload(stateDuration, lowerCaseFrom);
          } else if (from === State.SETUP) {
            // Setting Player Startup Time to fixed value of 1ms.
            // We can not get correct Player Startup Time from PWX player, because it does not have an "onInitialized" event
            this.stateMachineCallbacks.setup(1, State.SETUP.toLowerCase());
          } else if (event === Event.PAUSE && from === State.STARTUP && to === State.READY) {
            // fired PAUSE event in STARTUP state indicates we are moving back to READY,
            // so we have to clean setTimeout, and we should not call any stateMachineCallbacks
            this.clearVideoStartTimeout();
          } else if (from !== State.ERROR && !isStartup) {
            // Default "onleavestate" state machine callback
            //
            // Every time, when we leave a state, we should try to call "state" callback,
            // to be able to make "a cut" and send analytics sample (if necessary)
            const defaultStateMachineCallbackFn = this.stateMachineCallbacks[lowerCaseFrom];
            if (typeof defaultStateMachineCallbackFn === 'function') {
              // a try-catch block to add more resilience against callback errors
              try {
                defaultStateMachineCallbackFn(stateDuration, lowerCaseFrom, eventData);
              } catch (e) {
                logger.error('Exception occurred in State Machine callback ' + lowerCaseFrom, eventData, e);
              }
            } else {
              // if callback is not defined, log a warning to indicate to the developer that
              //  - a new callback function needs to be implemented to relay on this default state machine callback
              //  - or if no callback is needed, we should
              //    - early return from this JSM callback
              //    - or add this state to the ignored list
              logMissingCallbackWarning(from, [State.READY]);
            }
          }
        },

        [`onenter${State.PLAYING}`]: () => {
          this.playingHeartbeatService.startHeartbeat();
        },

        onenterstate: (event, from, to, eventTimestamp, _eventData) => {
          logStateMachineTransition('ENTER', event, from, to);

          this.onEnterStateTimestamp = eventTimestamp ?? Date.now();
          // if customer set custom "start time" timestamp in AnalyticsStateMachineOptions of adapter constructor,
          // use it and override `onEnterStateTimestamp` during JSM initialization (transition to initial SETUP state)
          if (isNumber(opts.starttime) && event === JSM_INITIAL_EVENT) {
            this.onEnterStateTimestamp = opts.starttime;
          }
        },

        [`onafter${Event.ERROR}`]: (_event, _from, _to, _eventTimestamp, eventData) => {
          this.stateMachineCallbacks.error(eventData);
        },
      },
    });
  }

  sourceChange(_config: AnalyticsConfig, _timestamp: number, _currentTime?: number): void {
    throw new Error('Method not implemented.');
  }

  addStatesToLog(
    event: string | undefined,
    from: string | undefined,
    to: string | undefined,
    timestamp: number,
    eventObject: any,
  ) {
    if (this.enabledDebugging) {
      this.debuggingStates.push(new EventDebugging(event, from, to, timestamp, eventObject));
    }
  }

  getStates() {
    return this.debuggingStates;
  }

  setEnabledDebugging(enabled: boolean) {
    this.enabledDebugging = enabled;
  }

  private maybeSetOrClearVideoStartTimeouts(from: string | undefined, to: string | undefined) {
    if (from === State.READY && to === State.STARTUP) {
      this.setVideoStartTimeout();
    } else if (from === State.STARTUP && to === State.PLAYING) {
      this.clearVideoStartTimeout();
    }
  }
}
